import {PlaceTypeEnumType} from '@wix/ambassador-seating-v1-seating-plan/types'
import {seatingsPricingOptionModalOpened, seatingsPricingOptionModalSelected} from '@wix/bi-logger-events-uou/v2'
import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {getPricingOption, getPricingOptionLabel, getPricingOptions} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {Dropdown, DropdownTheme, InputDialog, Text} from '@wix/wix-ui-tpa/cssVars'
import {useEnvironment, useTranslation, useVisitorBi} from '@wix/yoshi-flow-editor'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useCurrencyFormatter} from '../../../../../../../../commons/hooks/currency'
import {useAppActions, useAppSelector} from '../../../../../hooks/runtime'
import {getEvent} from '../../../../../selectors/event'
import {getLastKnownOrigin, getShowPricingOptionsPlace} from '../../../../../selectors/seating/mode'
import {getWholeTableReservationPrice} from '../../../../../selectors/seating/place'
import {getFees, withAdditionalFeesNote} from '../../../../../selectors/tax-and-fee'
import {TicketInfoTexts} from '../../ticket/ticket-info/ticket-info-texts/ticket-info-texts'
import {getSeatTypeTitle} from '../../utils'
import {Footer} from '../footer/footer'
import {classes, st} from './table-pricing-options-modal.st.css'

export const TablePricingOptionsModal = () => {
  const {t} = useTranslation()
  const {isMobile} = useEnvironment()
  const bi = useVisitorBi()
  const {setShowPricingOptionsPlace, selectPlacePricingOption} = useAppActions()
  const {getFormattedMoney, getTicketPriceText} = useCurrencyFormatter()

  const place = useAppSelector(getShowPricingOptionsPlace)
  const event = useAppSelector(getEvent)
  const origin = useAppSelector(getLastKnownOrigin)

  const pricingOptions = getPricingOptions(place.ticket)
  const placesCount = place.places.length
  const biFields = {
    pricing_options: JSON.stringify(pricingOptions),
    ticket_definition_id: place.ticket.id,
    is_whole_table: true,
    seat_guid: null,
    element_type: place.elementType,
    element_guid: place.planPlaceId,
    event_id: event.id,
  }

  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([
    ...new Array(placesCount).fill(pricingOptions[0].id),
  ])

  useEffect(() => {
    focusElement({selector: `${hookToAttributeSelector(DH.PRICING_OPTIONS_DROPDOWN)} button`, canFocus: true})
    bi.report(seatingsPricingOptionModalOpened(biFields))
  }, [])

  const close = useCallback(() => {
    setShowPricingOptionsPlace(null)
    bi.report(
      seatingsPricingOptionModalSelected({
        selection: 'Close',
        ...biFields,
      }),
    )
  }, [setShowPricingOptionsPlace])

  const confirm = useCallback(() => {
    selectPlacePricingOption({placeId: place.id, pricingOptionIds: selectedOptionIds, origin})
    bi.report(
      seatingsPricingOptionModalSelected({
        selection: 'Add to basket',
        selected_pricing_options: JSON.stringify(
          selectedOptionIds.map(selectedOptionId => pricingOptions.find(({id}) => id === selectedOptionId)),
        ),
        ...biFields,
      }),
    )
    setShowPricingOptionsPlace(null)
  }, [place.id, selectedOptionIds, selectPlacePricingOption, setShowPricingOptionsPlace])

  const onChange = (optionId: string, index: number) => {
    const newSelections = [...selectedOptionIds]
    newSelections[index] = optionId
    setSelectedOptionIds(newSelections)
  }

  const options = useMemo(
    () =>
      pricingOptions.map(option => ({
        id: option.id,
        value: getPricingOptionLabel(place.ticket, option.id, getFormattedMoney),
        subtitle: withAdditionalFeesNote(
          getFees({
            event,
            ticket: place.ticket,
            t,
            getFormattedMoney,
            overridePrice: option.price.amount,
          }),
          {event, ticket: place.ticket, t},
        ),
        isSelectable: true,
      })),
    [pricingOptions, event, place.ticket],
  )

  const getSelectedOptionAriaLabel = (optionId: string) => {
    const option = options.find(({id}) => id === optionId)
    return `${option.value} ${option.subtitle}`
  }

  return (
    <InputDialog
      title={t('seating_priceOptionsTable_title')}
      isOpen={true}
      onClose={close}
      fullscreen={isMobile}
      className={isMobile ? classes.mobileModalRoot : undefined}
      data-hook={DH.PRICING_OPTIONS_MODAL}
      closeButtonAriaLabel={t('a11y.close')}
      ariaLabel={t('seating_priceOptionsTable_title')}
      customFooter={
        <Footer
          primaryButtonLabel={t('seating_priceOptionsTable_confirmButton')}
          primaryButtonClick={confirm}
          secondaryButtonLabel={t('seating_priceOptionsTable_cancelButton')}
          secondaryButtonClick={close}
        >
          <Text tagName="div" className={classes.wholeReservationLabel}>
            {t('seating_tableReservation', {
              price: getWholeTableReservationPrice({
                event,
                place: {...place, selectedPricingOptionIds: selectedOptionIds},
                t,
                getFormattedMoney,
                getTicketPriceText,
              }),
            })}
          </Text>
        </Footer>
      }
    >
      <div className={st(classes.optionsContainer, {mobile: isMobile})}>
        {isMobile ? (
          <div className={classes.ticketInfo}>
            <TicketInfoTexts place={place} inPricingOptionsModal />
          </div>
        ) : null}
        {place.places.map((seat, index) => {
          const overridePrice = getPricingOption(place.ticket, selectedOptionIds[index]).price.amount
          const label =
            seat.type === PlaceTypeEnumType.STANDARD
              ? t('seating_priceOptionsTable_seatLabel', {number: seat.label})
              : t('seating_priceOptionsTable_seatLabel_withType', {
                  number: seat.label,
                  type: getSeatTypeTitle(seat.type, t),
                })

          return (
            <div className={classes.optionContainer} key={seat.id} data-hook={DH.SEAT_PRICING_OPTION_CONTAINER}>
              <Dropdown
                data-hook={`${DH.PRICING_OPTIONS_DROPDOWN}-${index}`}
                className={classes.option}
                theme={DropdownTheme.Box}
                newErrorMessage
                // @ts-expect-error
                errorAppearance="BackgroundAndBorder"
                mobileNativeSelect
                initialSelectedId={selectedOptionIds[index]}
                label={label}
                options={options}
                onChange={({id}) => onChange(id, index)}
                aria-label={`${label}, ${getSelectedOptionAriaLabel(selectedOptionIds[index])}`}
              />
              <Text tagName="div" className={classes.fees} data-hook={DH.WIX_FEE}>
                {withAdditionalFeesNote(
                  getFees({
                    event,
                    ticket: place.ticket,
                    t,
                    getFormattedMoney,
                    overridePrice,
                  }),
                  {event, ticket: place.ticket, t},
                )}
              </Text>
            </div>
          )
        })}
      </div>
    </InputDialog>
  )
}
